import { Amplify } from 'aws-amplify';
import { environment } from '../../../environments/environment';

interface AmplifyEnvConfig {
  userPoolId: string;
  userPoolClientId: string;
  identityPoolId?: string;
  region: string;
  oauth?: {
    domain: string;
    scope: string[];
    redirectSignIn: string;
    redirectSignOut: string;
    responseType: string;
  };
}

function buildRedirectUrls(configuredUrl: string): string[] {
  if (environment.production) {
    return [configuredUrl];
  }

  try {
    const configured = new URL(configuredUrl);
    const currentOrigin = window.location.origin;

    if (configured.origin === currentOrigin) {
      return [configuredUrl];
    }

    const sameOriginUrl = `${currentOrigin}${configured.pathname}${configured.search}${configured.hash}`;
    return [configuredUrl, sameOriginUrl];
  } catch {
    return [configuredUrl];
  }
}

function applyAmplifyConfig(config: AmplifyEnvConfig, isSaml: boolean): void {
  if (!config?.userPoolId || !config?.userPoolClientId) {
    console.warn('AWS Amplify configuration is incomplete. Please configure userPoolId and userPoolClientId.');
    return;
  }

  const cognitoConfig: any = {
    userPoolId: config.userPoolId,
    userPoolClientId: config.userPoolClientId,
    loginWith: {
      email: true,
      username: true,
    },
    signUpVerificationMethod: 'code' as const,
    userAttributes: {
      email: {
        required: true,
      },
      name: {
        required: false,
      },
    },
  };

  if (config.identityPoolId) {
    cognitoConfig.identityPoolId = config.identityPoolId;
  }

  if (config.oauth) {
    const oauthConfig: any = {
      domain: config.oauth.domain,
      scopes: config.oauth.scope,
      redirectSignIn: buildRedirectUrls(config.oauth.redirectSignIn),
      redirectSignOut: buildRedirectUrls(config.oauth.redirectSignOut),
      responseType: config.oauth.responseType,
    };

    if (isSaml && environment.samlProviderName) {
      oauthConfig.providers = [{ custom: environment.samlProviderName }];
    }

    cognitoConfig.loginWith.oauth = oauthConfig;
  }

  const amplifyAppConfig: any = {
    Auth: {
      Cognito: cognitoConfig,
    },
  };

  try {
    Amplify.configure(amplifyAppConfig);
    console.log(`AWS Amplify configured successfully (${isSaml ? 'SAML' : 'native'})`);
  } catch (error) {
    console.error('Error configuring AWS Amplify:', error);
  }
}

export function configureAmplify(): void {
  if (environment.samlEnabled && environment.saml) {
    const isLoginAdminPage = window.location.pathname === '/login-admin';
    const urlParams = new URLSearchParams(window.location.search);
    const isOAuthCallback = urlParams.has('code');

    if (isLoginAdminPage) {
      applyAmplifyConfig(environment.amplify, false);
    } else if (isOAuthCallback) {
      applyAmplifyConfig(environment.saml as AmplifyEnvConfig, true);
    } else {
      const storedAuthType = localStorage.getItem('app_auth_type');
      if (storedAuthType === 'native') {
        applyAmplifyConfig(environment.amplify, false);
      } else {
        applyAmplifyConfig(environment.saml as AmplifyEnvConfig, true);
      }
    }
  } else {
    applyAmplifyConfig(environment.amplify, false);
  }
}

export function reconfigureAmplifyForNative(): void {
  applyAmplifyConfig(environment.amplify, false);
}

export function reconfigureAmplifyForSaml(): void {
  if (environment.saml) {
    applyAmplifyConfig(environment.saml as AmplifyEnvConfig, true);
  }
}
